import { useEffect } from 'react';
import RTQuestionRow from './RTQuestionRow';

// id is index-based (leaf-N), not slugified from the leaf's own text —
// simpler than escaping Finnish/punctuation-heavy leaf names, and safe
// because this only ever renders one group's leaves at a time, so the
// index is unambiguous within the page at any given moment.
export const leafAnchorId = (leafIndex) => `kayttoika-leaf-${leafIndex}`;

// Content side of the table-of-contents layout: every leaf section of the
// active group renders in full, in place, instead of one leaf expanding
// at a time — the sidebar scrolls to a leaf's anchor rather than
// swapping it in, so nothing here ever needs to hide/show on its own.
export default function RTKayttoikaMain({ groupEntry, answers, rakennuksenIka, onToggleOption, onPickRasitusluokka, onVisibleLeafChange }) {
  // Scroll-spy: the sidebar used to only reflect whichever leaf was last
  // clicked, so scrolling through the list by hand left it pointing at
  // the wrong (first-clicked) section. Watches every leaf's own anchor
  // and reports whichever one is topmost in the "active" band near the
  // top of the viewport — root:null still correctly accounts for being
  // clipped by RTKayttoikaModal's own scroll container, not just the
  // bare page, so this works in both usages unchanged.
  useEffect(() => {
    if (!groupEntry || !onVisibleLeafChange) return undefined;
    const elementToLeaf = new Map();
    groupEntry.leafSections.forEach((_, li) => {
      const el = document.getElementById(leafAnchorId(li));
      if (el) elementToLeaf.set(el, li);
    });
    if (elementToLeaf.size === 0) return undefined;

    const visible = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const li = elementToLeaf.get(entry.target);
        if (li === undefined) return;
        if (entry.isIntersecting) visible.add(li);
        else visible.delete(li);
      });
      if (visible.size > 0) onVisibleLeafChange(Math.min(...visible));
    }, { rootMargin: '-10% 0px -80% 0px', threshold: 0 });

    elementToLeaf.forEach((_li, el) => observer.observe(el));
    return () => observer.disconnect();
  }, [groupEntry, onVisibleLeafChange]);

  if (!groupEntry) return null;

  return (
    <div>
      {groupEntry.leafSections.map(({ leaf, questions }, li) => (
        <div key={leaf} id={leafAnchorId(li)} style={{ scrollMarginTop: 12, marginBottom: 26 }}>
          <div style={{ fontSize: '0.98rem', fontWeight: 700, padding: '6px 2px 10px', borderBottom: '1px solid #e5e5ea', marginBottom: 6 }}>
            {leaf}
          </div>
          {questions.map(q => (
            <RTQuestionRow
              key={q.code}
              question={q}
              selectedOptions={answers[q.code] ?? {}}
              rakennuksenIka={rakennuksenIka}
              onToggleOption={(optionId) => onToggleOption(q.code, optionId)}
              onPickRasitusluokka={(optionId, rl) => onPickRasitusluokka(q.code, optionId, rl)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
